'use client';

import { usePathname, Link } from "@/i18n/navigation";
import { useEffect, useState } from "react";
import { XMarkIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { getLocalStorage, setLocalStorage } from "../../lib/StorageHelper";
import { initTracking } from "../../lib/tracking";

function ToggleSwitch({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange && onChange(!checked)}
      className={`relative h-7 w-12 shrink-0 rounded-full border transition-colors duration-200 ${
        disabled
          ? "cursor-not-allowed border-cta-300/40 bg-cta-700/40"
          : checked
          ? "border-cta-300 bg-cta-700"
          : "border-white/30 bg-white/10"
      }`}
    >
      <span
        className={`absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-white shadow transition-all duration-200 ${
          checked ? "left-[26px]" : "left-1"
        }`}
      />
    </button>
  );
}

const CookieBanner = () => {
  const pathname = usePathname();
  const [cookieConsent, setCookieConsent] = useState(null);
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [view, setView] = useState("main"); // "main" | "customize"
  const [analyticsChoice, setAnalyticsChoice] = useState(false);

  useEffect(() => {
    const storedCookieConsent = getLocalStorage("cookie_consent", null);

    if (storedCookieConsent === null) {
      setShowConsentBanner(true);
      return;
    }

    setCookieConsent(
      storedCookieConsent === true || storedCookieConsent === "true"
    );
  }, []);

  useEffect(() => {
    if (cookieConsent === null) return;

    const consentValue = cookieConsent ? "granted" : "denied";

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };

    window.gtag("consent", "update", {
      ad_user_data: consentValue,
      ad_personalization: consentValue,
      ad_storage: consentValue,
      analytics_storage: consentValue,
    });

    setLocalStorage("cookie_consent", cookieConsent);
  }, [cookieConsent]);

  const applyChoice = (granted) => {
    // Persisted synchronously so initTracking() sees the accepted consent
    // immediately, instead of waiting for the effect above to run.
    setLocalStorage("cookie_consent", granted);
    setCookieConsent(granted);
    setShowConsentBanner(false);
    if (granted) initTracking();
  };

  const acceptAll = () => applyChoice(true);
  const refuseAll = () => applyChoice(false);
  const closeWithoutConsent = () => applyChoice(false);

  const saveChoices = () => applyChoice(analyticsChoice);

  const openCustomize = () => {
    setAnalyticsChoice(false);
    setView("customize");
  };

  if (pathname?.startsWith("/admin")) return null;
  if (!showConsentBanner) return null;

  return (
    <div className="fixed inset-x-0 bottom-[92px] z-[9999] px-4 sm:bottom-6 sm:px-6">
      <div
        className="mx-auto max-w-4xl overflow-hidden rounded-3xl border-2 border-brand-600/50 shadow-[0_25px_80px_rgba(0,0,0,0.45)]"
        style={{
          background:
            "linear-gradient(135deg, rgba(31,73,109,0.5), rgba(38,84,123,0.5))",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
        }}
      >
        <div className="h-2 w-full bg-gradient-to-r from-cta-800 via-cta-600 to-cta-800" />

        <div className="relative px-5 py-5 sm:px-7 sm:py-6">
          <div className="pointer-events-none absolute right-4 top-4 hidden h-24 w-24 rounded-full bg-white/10 blur-2xl sm:block" />
          <div className="pointer-events-none absolute bottom-0 left-6 hidden h-20 w-20 rounded-full bg-white/10 blur-2xl sm:block" />

          <button
            type="button"
            onClick={closeWithoutConsent}
            aria-label="Fermer sans accepter les cookies non essentiels"
            className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>

          {view === "main" && (
            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-center sm:text-left">
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-white/15 text-xl sm:mx-0">
                  🍪
                </div>

                <h3 className="text-xl font-extrabold leading-tight text-white sm:text-2xl">
                  Gestion des cookies
                </h3>

                <p className="mt-2 max-w-xl text-sm font-medium leading-relaxed text-white/90 sm:text-base">
                  Nous utilisons des{" "}
                  <span className="font-extrabold text-white">cookies</span>{" "}
                  pour améliorer votre expérience, mesurer les performances du
                  site et optimiser nos services.
                </p>

                <Link
                  href="/politique-de-confidentialite"
                  target="_blank"
                  className="mt-3 inline-block text-sm font-extrabold text-white/85 underline underline-offset-4 transition hover:text-white"
                >
                  Politique de confidentialité
                </Link>
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto sm:min-w-[320px] sm:flex-row">
                <button
                  type="button"
                  onClick={acceptAll}
                  className="w-full rounded-2xl border border-cta-300/70 bg-gradient-to-b from-cta-500 via-cta-600 to-cta-800 px-7 py-4 text-base font-extrabold text-white shadow-[0_20px_50px_rgba(26,127,75,0.45),inset_0_1px_0_rgba(255,255,255,0.3)] transition-all duration-300 hover:scale-[1.03] hover:brightness-110 hover:shadow-[0_24px_60px_rgba(26,127,75,0.55),inset_0_1px_0_rgba(255,255,255,0.35)] sm:w-auto"
                >
                  Tout accepter
                </button>

                <button
                  type="button"
                  onClick={openCustomize}
                  className="w-full rounded-xl border border-white/25 bg-white/10 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/20 sm:w-auto"
                >
                  Personnaliser
                </button>
              </div>
            </div>
          )}

          {view === "customize" && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setView("main")}
                className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-white/70 transition hover:text-white"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Retour
              </button>

              <h3 className="text-xl font-extrabold leading-tight text-white sm:text-2xl">
                Personnaliser les cookies
              </h3>

              <div className="mt-4 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/15 bg-white/5 p-4">
                  <div>
                    <p className="font-bold text-white">Cookies nécessaires</p>
                    <p className="mt-1 text-sm text-white/70">
                      Indispensables au fonctionnement du site (navigation,
                      sécurité). Toujours actifs.
                    </p>
                  </div>
                  <ToggleSwitch checked={true} disabled />
                </div>

                <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/15 bg-white/5 p-4">
                  <div>
                    <p className="font-bold text-white">
                      Mesure d&apos;audience &amp; marketing
                    </p>
                    <p className="mt-1 text-sm text-white/70">
                      Statistiques de visite et suivi des campagnes
                      publicitaires (Google Analytics, Google Ads).
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={analyticsChoice}
                    onChange={setAnalyticsChoice}
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={saveChoices}
                  className="w-full rounded-2xl border border-cta-300/70 bg-gradient-to-b from-cta-500 via-cta-600 to-cta-800 px-7 py-3.5 text-sm font-extrabold text-white shadow-[0_20px_50px_rgba(26,127,75,0.45),inset_0_1px_0_rgba(255,255,255,0.3)] transition-all duration-300 hover:scale-[1.03] hover:brightness-110 sm:w-auto"
                >
                  Enregistrer mes choix
                </button>

                <button
                  type="button"
                  onClick={refuseAll}
                  className="w-full rounded-xl border border-white/25 bg-white/10 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/20 sm:w-auto"
                >
                  Tout refuser
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default CookieBanner;
